import React, { useState, useEffect } from 'react';
import { visiteAPI } from '../../services/api';
import VisiteTab from './VisiteTab';
import { medicoDellaVisita } from '../../utils/medicoDellaVisita';

/* Timeline: trattamento PRIMA, poi patologia (es. "Filler · vuotezza") */
const nomeTimeline = (v) => {
    const pat = (v.patologia || '').trim();
    const tra = (v.trattamento || '').trim();
    if (tra && pat) return `${tra} · ${pat}`;
    return tra || pat || 'Procedura';
};
/* Recap: SOLO trattamento */
const nomeRecap = (v) => (v.trattamento || '').trim() || (v.patologia || '').trim() || 'Procedura';

const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const MESI = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];
const fmtMese = (d) => { const x = new Date(d); return `${MESI[x.getMonth()]} ${x.getFullYear()}`; };

const YEAR_COLORS = ['#17a2a2', '#e6007e', '#f4a300', '#6a5acd', '#2e8b57', '#d2691e', '#3b7dd8', '#c0392b'];
const colorForYear = (year, years) => YEAR_COLORS[years.indexOf(year) % YEAR_COLORS.length];

const IconaProc = ({ color }) => (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={color}
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 20l6-6M9 11l4 4M12 8l4 4M14 4l6 6-2 2-6-6 2-2Z" />
    </svg>
);

const ProcedureMediche = ({ pazienteId, sessoPaziente, visitaDaAprire, onVisitaAperta,
                            nuovaVisitaRichiesta = 0, onNuovaVisitaPresa }) => {
    const [tab, setTab] = useState('elenco');
    const [visite, setVisite] = useState([]);
    const [loading, setLoading] = useState(true);
    const [focus, setFocus] = useState(null);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [pazienteId]);

    /* Una visita indicata da fuori apre l'elenco su di lei. Si aspetta che
       l'elenco abbia finito di caricare: darle il fuoco prima vorrebbe
       dire cercare nella pagina una riga che non c'e' ancora. */
    useEffect(() => {
        if (!visitaDaAprire || loading) return;
        setFocus(visitaDaAprire);
        setTab('elenco');
        if (onVisitaAperta) onVisitaAperta();
        /* eslint-disable-next-line */
    }, [visitaDaAprire, loading]);

    /* «Nuova visita» dall'intestazione della cartella: la visita nasce
       nell'elenco, anche se si stava guardando la timeline o il recap. */
    useEffect(() => {
        if (nuovaVisitaRichiesta) setTab('elenco');
    }, [nuovaVisitaRichiesta]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await visiteAPI.getByPazienteId(pazienteId);
            if (res.data.success) setVisite(res.data.data.visite || []);
        } catch (e) {
            console.error('Errore caricamento procedure', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (visitaId) => { setFocus(visitaId); setTab('elenco'); };

    return (
        <div className="pm-wrap">
            <div className="pm-tabs">
                <button className={tab === 'elenco' ? 'active' : ''} onClick={() => setTab('elenco')}>Elenco</button>
                <button className={tab === 'timeline' ? 'active' : ''} onClick={() => setTab('timeline')}>Timeline</button>
                <button className={tab === 'recap' ? 'active' : ''} onClick={() => setTab('recap')}>Recap</button>
            </div>

            {tab === 'elenco' && <VisiteTab sessoPaziente={sessoPaziente} pazienteId={pazienteId} focusVisitaId={focus}
                                            nuovaVisitaRichiesta={nuovaVisitaRichiesta} onNuovaVisitaPresa={onNuovaVisitaPresa} />}
            {tab === 'timeline' && <Timeline visite={visite} loading={loading} onSelect={handleSelect} />}
            {tab === 'recap' && <Recap visite={visite} loading={loading} onSelect={handleSelect} />}
        </div>
    );
};

/* singola freccia-procedura (dettaglio anno) */
const StepChevron = ({ v, color, onSelect }) => (
    <div className="pm-tl-step" onClick={() => onSelect(v.id)} title="Apri questa procedura">
        <div className="pm-tl-circle" style={{ borderColor: color }}>
            <IconaProc color={color} />
        </div>
        <div className="pm-tl-pin" style={{ borderTopColor: color }} />
        <div className="pm-tl-chevron" style={{ background: color }}>
            {fmtMese(v.data_visita)}
        </div>
        <div className="pm-tl-caption">
            <div className="pm-tl-cap-nome" style={{ color }}>{nomeTimeline(v)}</div>
            <div className="pm-tl-cap-data">{fmt(v.data_visita)}</div>
            {v.medico_cognome && (
                <div className="pm-tl-cap-medico">{medicoDellaVisita(v)}</div>
            )}
        </div>
    </div>
);

/* ---------------- TIMELINE a 2 livelli ---------------- */
const Timeline = ({ visite, loading, onSelect }) => {
    const [openYears, setOpenYears] = useState({});
    if (loading) return <div className="sp-loading">Caricamento…</div>;
    if (!visite.length) return <Vuoto />;

    const sorted = [...visite].sort((a, b) => new Date(a.data_visita) - new Date(b.data_visita));
    const years = [...new Set(sorted.map(v => new Date(v.data_visita).getFullYear()))].sort();

    const byYear = {};
    sorted.forEach(v => {
        const y = new Date(v.data_visita).getFullYear();
        (byYear[y] = byYear[y] || []).push(v);
    });

    const toggleYear = (y) => setOpenYears(prev => ({ ...prev, [y]: !prev[y] }));

    return (
        <div className="pm-tl">
            {/* livello 1: anni compatti (cliccabili) */}
            <div className="pm-yr-row">
                {years.map(y => {
                    const c = colorForYear(y, years);
                    const cnt = byYear[y].length;
                    return (
                        <button key={y}
                            className={`pm-yr-chip ${openYears[y] ? 'open' : ''}`}
                            style={{ background: c }}
                            onClick={() => toggleYear(y)}>
                            <span className="pm-yr-num">{y}</span>
                            <span className="pm-yr-count">{cnt} {cnt === 1 ? 'procedura' : 'procedure'}</span>
                            <span className="pm-yr-caret">▾</span>
                        </button>
                    );
                })}
            </div>

            {/* livello 2: dettaglio per ogni anno aperto (anche più anni insieme) */}
            {years.filter(y => openYears[y]).map(y => {
                const c = colorForYear(y, years);
                return (
                    <div key={y} className="pm-yr-detail">
                        <div className="pm-yr-detail-head" style={{ color: c }}>
                            <span className="pm-yr-detail-dot" style={{ background: c }} />
                            {y} — {byYear[y].length} {byYear[y].length === 1 ? 'procedura' : 'procedure'}
                        </div>
                        <div className="pm-tl-flow">
                            {byYear[y].map(v => (
                                <StepChevron key={v.id} v={v} color={c} onSelect={onSelect} />
                            ))}
                        </div>
                    </div>
                );
            })}

            {years.every(y => !openYears[y]) && (
                <div className="pm-tl-hint">Clicca un anno per aprire il dettaglio delle procedure →</div>
            )}
        </div>
    );
};

/* ---------------- RECAP accordion ---------------- */
const Recap = ({ visite, loading, onSelect }) => {
    const [open, setOpen] = useState({});
    if (loading) return <div className="sp-loading">Caricamento…</div>;
    if (!visite.length) return <Vuoto />;

    const map = {};
    visite.forEach(v => {
        const nome = nomeRecap(v);
        const d = new Date(v.data_visita);
        if (!map[nome]) map[nome] = { nome, count: 0, min: d, max: d, items: [] };
        const m = map[nome];
        m.count++;
        m.items.push(v);
        if (d < m.min) m.min = d;
        if (d > m.max) m.max = d;
    });
    const rows = Object.values(map).sort((a, b) => b.max - a.max);
    rows.forEach(r => r.items.sort((a, b) => new Date(b.data_visita) - new Date(a.data_visita)));

    const toggle = (nome) => setOpen(prev => ({ ...prev, [nome]: !prev[nome] }));

    return (
        <div className="rc-list">
            {rows.map((r, i) => (
                <div key={i} className={`rc-item ${open[r.nome] ? 'open' : ''}`}>
                    <button className="rc-head" onClick={() => toggle(r.nome)}>
                        <span className="rc-nome">{r.nome}</span>
                        <span className="rc-meta">
                            {r.count} {r.count === 1 ? 'volta' : 'volte'} · dal {fmt(r.min)} al {fmt(r.max)}
                        </span>
                        <span className="rc-chev">▾</span>
                    </button>
                    <div className="rc-body">
                        {r.items.map(v => (
                            <button key={v.id} className="rc-occ" onClick={() => onSelect(v.id)}
                                    title="Apri questa procedura">
                                <span className="rc-occ-data">{fmt(v.data_visita)}</span>
                                <span className="rc-occ-info">
                                    {v.patologia ? v.patologia : nomeTimeline(v)}
                                    {v.medico_cognome ? ` · Dr. ${v.medico_cognome} ${v.medico_nome}` : ''}
                                </span>
                                <span className="rc-occ-arrow">›</span>
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const Vuoto = () => (
    <div className="sp-placeholder">
        <div className="sp-placeholder-badge">Nessun dato</div>
        <p>Nessuna procedura registrata per questo paziente.</p>
    </div>
);

export default ProcedureMediche;
